import React from 'react';
import Root from '@/routes/root.tsx';
import { createBrowserRouter, RouterProvider, Navigate, redirect } from 'react-router-dom';
import NotFound from '@/pages/NotFound/NotFound.tsx';
import Dashboard from '@/pages/Dashboard/Dashboard.tsx';
import Clients from '@/pages/NewClients/Clients';
import Settings from '@/pages/Settings/Settings.tsx';
import LoginForm from '@/pages/Login/loginForm.tsx';
import useAuthStore from '@/lib/auth';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES } from '@/constants/languages';
import ClientProspectus from '@/pages/NewClients/ClientProspectusDetails';
import { getLanguageFromPath } from '@/utils/language';
import App from './App';

const Router: React.FC = () => {
	const { isAuthenticated } = useAuthStore();
	const language = getLanguageFromPath(window.location.pathname);

	// Auth guard loader
	const protectedLoader = async () => {
		if (!isAuthenticated()) {
			return redirect('/signin');
		}
		return null;
	};
	// Redirect authenticated users from signin page
	const publicLoader = async () => {
		if (isAuthenticated()) {
			return redirect(`/${language}/center/clients`);
		}
		return null;
	};

	const languageMiddleware = async ({ request, params }) => {
		const { lang } = params;

		if (!lang || !SUPPORTED_LANGUAGES.includes(lang)) {
			const url = new URL(request.url);
			const pathname = url.pathname;
			const segments = pathname.split('/');

			if (segments.length === 2 && !segments[1]) {
				return redirect(`/${DEFAULT_LANGUAGE}`);
			}

			if (!SUPPORTED_LANGUAGES.includes(segments[1])) {
				segments[1] = DEFAULT_LANGUAGE;
				return redirect(segments.join('/'));
			}
		}

		return null;
	};

	const router = createBrowserRouter([
		{
			path: '/',
			element: <Navigate to={`/${DEFAULT_LANGUAGE}`} replace />,
			errorElement: <Navigate to={`/${language}`} replace />
		},
		{
			path: '/:lang',
			loader: languageMiddleware,
			element: <App />,
			errorElement: <NotFound />,
			children: [
				{
					index: true,
					element: <Navigate to={`signin`} replace />
				},
				{
					path: 'signin',
					element: <LoginForm />,
					errorElement: <NotFound />,
					loader: publicLoader
				},
				{
					path: 'center',
					element: <Root />,
					errorElement: <NotFound />,
					loader: protectedLoader,
					children: [
						{
							index: true,
							element: <Navigate to={`dashboard`} replace />
						},
						{
							path: 'dashboard',
							element: <Dashboard />,
							loader: async () => {
								// You can load data here for the dashboard
								return { someData: 'dashboard data' };
							}
						},
						{
							path: 'clients',
							element: <Clients />,
							loader: async () => {
								// You can load data here for the dashboard
								return { someData: 'clients data' };
							},
							children: [
								{
									path: 'clients/:clientId',
									element: <ClientProspectus />
								}
							]
						},
						{
							path: 'settings',
							element: <Settings />,
							loader: async () => {
								// Load settings data
								return { settingsData: 'settings data' };
							}
						}
					]
				}
			]
		}
	]);
	return <RouterProvider router={router} />;
};
export default Router;
